import {view} from '@sail/ui';
import {useRef, useState, useEffect} from 'react';
import {animate, AnimatePresence} from '@sail/engine';
import {useTheme} from '../../ThemeProvider';
import {dotComColors, dotComTransitions} from '../../theme/dotCom';

type Props = {
  background: 'neutral100' | 'neutral600';
  borderTop?: string;
  children: React.ReactNode;
  animation: 'all' | 'disabled' | 'skip-scale';
};

export const DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS = 200;

/**
 * How the chat bubble animation works:
 * 1. The bubble scale in 200ms
 * 2. The initial text is hidden until the scale animation is done, then it's shown and the bubble container starts growing in width
 * 3. Text appears in a single line until the width animation is done and the word streamer reveals it word by word
 */
export const ChatBubbleDotCom = ({
  background,
  borderTop,
  children,
  animation,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [widthTransitionComplete, setWidthTransitionComplete] = useState(
    animation === 'disabled',
  );

  useEffect(() => {
    if (!ref.current || animation === 'disabled') {
      return;
    }

    const animatingElements = ref.current.getAnimations();
    const animationPromises = animatingElements.map(
      (elAnimation) => elAnimation.finished,
    );

    Promise.all(animationPromises).then(() => setWidthTransitionComplete(true));
  }, [animation]);

  const theme = useTheme();
  return (
    <AnimatePresence>
      <view.div
        ref={ref}
        data-testid="chat-bubble"
        css={{
          opacity: 1,
          transform: 'scale(1)',
          transformOrigin: 'bottom left',
          maxWidth: '310px',
          minWidth: '52px',
          minHeight: '36px',
          borderRadius: 'medium',
          whiteSpace: widthTransitionComplete ? 'pre-line' : 'nowrap',
          overflow: widthTransitionComplete ? 'visible' : 'hidden',
          background:
            background === 'neutral100'
              ? dotComColors.brand700
              : theme.linkColor,
          paddingX: '14px',
          paddingY: 'small',
          borderTop: borderTop || '',
          overflowWrap: 'break-word',
          stack: 'y',
          gap: 'small',
          '& a': {
            transition: dotComTransitions.buttonTransition,
          },
          '& a:hover': {
            color: dotComColors.brand500,
          },
          '& a:focus-visible': {
            outline: theme.focusVisibleOutline,
          },
        }}
        uses={[
          animate.in({
            duration: DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS,
            opacity: 0,
            transform: 'scale(0.6)',
            easing: 'ease-out',
            disabled: animation === 'disabled' || animation === 'skip-scale',
          }),
          animate.in({
            duration: DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS,
            delay: DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS,
            maxWidth: '52px',
            easing: 'ease-out',
            disabled: animation === 'disabled',
          }),
        ]}
      >
        {animation === 'disabled' ? (
          children
        ) : (
          <view.div
            css={{opacity: '1'}}
            uses={[
              animate.in({
                duration: 0,
                delay: DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS,
                opacity: '0',
                easing: 'ease-out',
              }),
            ]}
          >
            {children}
          </view.div>
        )}
      </view.div>
    </AnimatePresence>
  );
};
